// Single source of truth for the work grid on the homepage and the
// /work/[slug] case study pages.

export interface ProjectResult {
    value: string
    label: string
}

export interface Project {
    slug: string
    name: string
    client: string
    category: string
    summary: string
    image: string
    imageAlt: string
    shareImage: string
    color: string
    services: string[]
    year: string
    timeline: string
    challenge: string
    approach: string[]
    deliverables: string[]
    results: ProjectResult[]
    quote: {
        text: string
        name: string
        role: string
    }
}

export const PROJECTS: Project[] = [
    {
        slug: 'bloom-ritual',
        name: "Bloom Ritual",
        client: "Bloom Ritual Skincare",
        category: "Branding & packaging",
        summary: "A calm, confident identity and packaging system for a plant-based skincare startup heading into retail.",
        image: '/images/work-skincare.jpg',
        shareImage: 'https://huzzah-studio.cdn.modulify.website/share/work-skincare.jpg',
        imageAlt: "Bloom Ritual skincare bottles and jars arranged on a wooden stand",
        color: 'bg-bubblegum',
        services: ["Brand strategy", "Visual identity", "Packaging", "Launch content"],
        year: "2025",
        timeline: "6 weeks",
        challenge: "Bloom Ritual's formulas were excellent, but their homemade labels made them look like a craft-fair side project. With a retail deal on the table, they needed packaging that could hold its own next to established brands without losing the warmth their early fans loved.",
        approach: [
            "We built the brand around one simple idea: skincare as a small daily ritual, not a twelve-step chore. That led to a quiet, natural palette, soft rounded type and a botanical mark that still works on something as small as a lip balm lid.",
            "The packaging system runs on a single grid across every product, so new items can be added in an afternoon. We also art-directed the launch shoot, so the brand looks consistent from the shelf to the feed."
        ],
        deliverables: ["Brand strategy", "Logo and botanical mark", "Packaging system for 9 products", "Launch photography direction"],
        results: [
            { value: "3x", label: "Wholesale orders in the first quarter" },
            { value: "9", label: "Products packaged in one system" },
            { value: "48h", label: "To sell out the first retail run" }
        ],
        quote: {
            text: "Fast, funny and frighteningly good at what they do. Our first packaging run sold out before we could restock.",
            name: "Sofia Marin",
            role: "Co-founder, Bloom Ritual"
        }
    },
    {
        slug: 'bluebird-roasters',
        name: "Bluebird Roasters",
        client: "Bluebird Roasters",
        category: "Packaging & online shop",
        summary: "From coffee cart to coffee brand: bold packaging and an online shop for a small-batch roaster.",
        image: '/images/work-coffee.jpg',
        shareImage: 'https://huzzah-studio.cdn.modulify.website/share/work-coffee.jpg',
        imageAlt: "Matte blue Bluebird Roasters coffee bag standing on a white surface",
        color: 'bg-sky',
        services: ["Visual identity", "Packaging", "Online shop", "Subscription launch"],
        year: "2025",
        timeline: "8 weeks",
        challenge: "Bluebird had a loyal line at their cart every morning, but online they were invisible. Their beans went out in plain brown pouches with a sticker, and their website was a single page with an email address on it.",
        approach: [
            "We gave Bluebird a signature color they could own: a deep, confident blue that stands out on any shelf. A simple bird mark and hand-drawn flavor notes make every roast easy to recognise and fun to collect.",
            "Then we built an online shop with a coffee subscription at its heart, so regulars can keep their favorite beans coming without thinking about it."
        ],
        deliverables: ["Logo refresh and color system", "Packaging for 6 roasts", "Custom online shop", "Subscription launch campaign"],
        results: [
            { value: "2x", label: "Online sales in the first quarter" },
            { value: "640", label: "Coffee subscribers in six months" },
            { value: "4.9", label: "Average product review score" }
        ],
        quote: {
            text: "They turned our little coffee cart into a brand people happily queue for. Online sales doubled in the first quarter after the rebrand.",
            name: "Marcus Bell",
            role: "Founder, Bluebird Roasters"
        }
    },
    {
        slug: 'kaleido',
        name: "Kaleido",
        client: "Kaleido",
        category: "Website & product design",
        summary: "A colorful new website and product look for a design collaboration app that needed to stand out in a crowded market.",
        image: '/images/work-website.jpg',
        shareImage: 'https://huzzah-studio.cdn.modulify.website/share/work-website.jpg',
        imageAlt: "Laptop showing the colorful Kaleido logo on a bright studio desk",
        color: 'bg-lavender',
        services: ["Brand refresh", "Website design", "Development", "Product UI"],
        year: "2024",
        timeline: "10 weeks",
        challenge: "Kaleido's app was loved by the people who used it, but its website looked like every other software site: blue gradients, vague promises and stock photos. Visitors weren't sticking around long enough to understand what made it special.",
        approach: [
            "We leaned into the name. A bold, faceted logo and a bright color system now carry from the website into the product itself, so the whole experience feels like one brand from the very first click.",
            "The new website shows the product in action within the first scroll, using short interactive demos instead of long paragraphs. Every page ends with one clear next step."
        ],
        deliverables: ["Logo and color system", "12-page marketing website", "Interactive product demos", "Product UI style guide"],
        results: [
            { value: "+40%", label: "Demo requests in the first month" },
            { value: "-35%", label: "Fewer visitors leaving after one page" },
            { value: "1.2s", label: "Average page load time" }
        ],
        quote: {
            text: "Huzzah gave us a website that finally sounds like us. Demo requests jumped 40% in the first month.",
            name: "Priya Nair",
            role: "Head of Marketing, Kaleido"
        }
    },
    {
        slug: 'fizzy-fern',
        name: "Fizzy Fern",
        client: "Fizzy Fern Botanical Soda",
        category: "Launch campaign",
        summary: "A playful launch campaign that took a new botanical soda from unknown to sold out in its first summer.",
        image: '/images/work-cans.jpg',
        shareImage: 'https://huzzah-studio.cdn.modulify.website/share/work-cans.jpg',
        imageAlt: "Pastel green Fizzy Fern soda can surrounded by floating peach and green spheres",
        color: 'bg-mint',
        services: ["Campaign concept", "Art direction", "Short-form video", "Paid social"],
        year: "2024",
        timeline: "5 weeks",
        challenge: "Fizzy Fern had a great-tasting drink, a tiny budget and a summer launch date that wasn't moving. They needed people to notice, try and share a brand nobody had heard of yet.",
        approach: [
            "We built the campaign around the can itself: soft pastel worlds, floating fruit and just enough weirdness to make people stop scrolling. Every piece was designed to be shared, not just seen.",
            "We shot a library of short videos in a single day, tested different hooks on social and moved the budget to whatever was working each week."
        ],
        deliverables: ["Campaign concept and tagline", "Product art direction", "24 short-form videos", "Paid social management"],
        results: [
            { value: "2M+", label: "Video views in the first week" },
            { value: "6x", label: "Return on ad spend" },
            { value: "31", label: "New stockists by the end of summer" }
        ],
        quote: {
            text: "Our launch video passed two million views in a week. I still don't fully know how they did it, and I'm not asking.",
            name: "Leo Fischer",
            role: "Brand Lead, Fizzy Fern"
        }
    }
]

export const getProject = (slug: string): Project | undefined => {
    return PROJECTS.find((project) => project.slug === slug)
}
